/**
 * Organization Service
 *
 * API service for organization CRUD operations.
 * Connects to FastAPI /api/organizations endpoints.
 */

import { apiClient } from '@shared/services/apiClient';
import { apiCall } from '@shared/utils/apiWrapper';
import type {
  OrganizationListResponse,
  OrganizationItem,
  OrganizationCreateRequest,
  OrganizationUpdateRequest,
  OrganizationListParams,
  SuccessResponse,
  ContactItem,
} from '@shared/types/api';

/**
 * Fetch paginated list of organizations
 *
 * @param params - Query parameters for filtering, sorting, and pagination
 * @returns Promise resolving to organization list with metadata
 *
 * @example
 * ```typescript
 * const result = await fetchOrganizations({
 *   page: 1,
 *   per_page: 20,
 *   search: 'Houston',
 *   has_address: true
 * });
 * ```
 */
export async function fetchOrganizations(
  params: OrganizationListParams = {}
): Promise<OrganizationListResponse> {
  return apiCall('fetch organizations', () =>
    apiClient.get<OrganizationListResponse>('/organizations', { params })
  );
}

/**
 * Fetch single organization by ID
 *
 * @param orgId - Organization ID
 * @returns Promise resolving to organization details
 *
 * @example
 * ```typescript
 * const org = await fetchOrganizationById(123);
 * console.log(org.name); // "ACME Corp"
 * ```
 */
export async function fetchOrganizationById(orgId: number): Promise<OrganizationItem> {
  return apiCall(`fetch organization ${orgId}`, () =>
    apiClient.get<OrganizationItem>(`/organizations/${orgId}`)
  );
}

/**
 * Create new organization
 *
 * @param data - Organization creation data
 * @returns Promise resolving to created organization
 *
 * @example
 * ```typescript
 * const newOrg = await createOrganization({
 *   name: 'New Company',
 *   address: '123 Main St, Houston, TX 77043',
 *   owner_id: 16122048
 * });
 * ```
 */
export async function createOrganization(
  data: OrganizationCreateRequest
): Promise<OrganizationItem> {
  return apiCall('create organization', () =>
    apiClient.post<OrganizationItem>('/organizations', data)
  );
}

/**
 * Update existing organization
 *
 * @param orgId - Organization ID
 * @param data - Organization update data (partial)
 * @returns Promise resolving to updated organization
 *
 * @example
 * ```typescript
 * const updated = await updateOrganization(123, {
 *   address: '456 New Address, Houston, TX 77001'
 * });
 * ```
 */
export async function updateOrganization(
  orgId: number,
  data: OrganizationUpdateRequest
): Promise<OrganizationItem> {
  return apiCall(`update organization ${orgId}`, () =>
    apiClient.put<OrganizationItem>(`/organizations/${orgId}`, data)
  );
}

/**
 * Delete organization
 *
 * @param orgId - Organization ID
 * @returns Promise resolving to success message
 *
 * @example
 * ```typescript
 * await deleteOrganization(123);
 * console.log('Organization deleted successfully');
 * ```
 */
export async function deleteOrganization(orgId: number): Promise<SuccessResponse> {
  return apiCall(`delete organization ${orgId}`, () =>
    apiClient.delete<SuccessResponse>(`/organizations/${orgId}`)
  );
}

/**
 * Fetch contacts for a specific organization
 *
 * @param orgId - Organization ID
 * @returns Promise resolving to contacts list
 *
 * @example
 * ```typescript
 * const contacts = await fetchOrganizationContacts(123);
 * console.log(contacts.length); // Number of contacts
 * ```
 */
export async function fetchOrganizationContacts(orgId: number): Promise<ContactItem[]> {
  return apiCall(`fetch contacts for organization ${orgId}`, () =>
    apiClient.get<ContactItem[]>(`/organizations/${orgId}/contacts`)
  );
}

/**
 * Fetch unique Pipedrive owners for filter dropdown
 *
 * @returns Promise resolving to sorted list of owners
 *
 * @example
 * ```typescript
 * const owners = await fetchOwners();
 * console.log(owners); // [{ id: 16122048, name: "Evan Roland", email: "..." }]
 * ```
 */
export async function fetchOwners(): Promise<Array<{ id: number; name: string; email: string }>> {
  return apiCall('fetch owners', () =>
    apiClient.get<Array<{ id: number; name: string; email: string }>>('/organizations/owners')
  );
}

/**
 * Fetch organizations for a specific campaign
 *
 * Bypasses current filter state and returns ALL organizations
 * associated with the campaign, regardless of 15DP, drop month, or drop type filters.
 * This ensures DMS parity where campaign organizations always appear
 * in the "Organizations Selected" table.
 *
 * @param campaignId - Campaign ID (e.g., "CAMPAIGN_20251028_121851")
 * @returns Promise resolving to organizations array
 *
 * @example
 * ```typescript
 * const orgs = await fetchCampaignOrganizations('CAMPAIGN_20251028_121851');
 * console.log(orgs.length); // 67 organizations
 * ```
 */
/**
 * Campaign organization as returned by the API
 * (different field names than OrganizationItem)
 */
interface CampaignOrganizationResponse {
  org_id: number;
  org_name: string;
  address: string | null;
}

export async function fetchCampaignOrganizations(campaignId: string): Promise<OrganizationItem[]> {
  // Fetch raw data using apiCall wrapper (which extracts response.data)
  const data = await apiCall(`fetch organizations for campaign ${campaignId}`, () =>
    apiClient.get<{ organizations: CampaignOrganizationResponse[]; total: number }>(
      `/campaigns/${campaignId}/organizations`
    )
  );

  // Guard against undefined/null response - return empty array if no organizations
  const organizations = data?.organizations || [];

  // Transform API response to match OrganizationItem type
  return organizations.map((org) => ({
    id: org.org_id,
    name: org.org_name,
    address: org.address,
    owner: null,
    city: null,
    next_activity_date: null,
    next_activity_type: null,
    quadrant: null,
  }));
}

/**
 * Save pending organization selections to backend
 *
 * Persists the user's selection state (preview, final, removal) for a campaign.
 * Enables session persistence across browser refreshes.
 *
 * @param campaignId - Campaign ID (e.g., "CAMPAIGN_20251028_121851")
 * @param selections - Selection state containing preview_ids, final_ids, removal_ids
 * @returns Promise resolving when save completes
 *
 * @example
 * ```typescript
 * await savePendingOrganizations('CAMPAIGN_20251028_121851', {
 *   preview_ids: [1, 2, 3],
 *   final_ids: [10, 20, 30],
 *   removal_ids: [5, 6],
 * });
 * ```
 */
export async function savePendingOrganizations(
  campaignId: string,
  selections: {
    preview_ids: number[];
    final_ids: number[];
    removal_ids: number[];
  }
): Promise<void> {
  return apiCall(`save pending organizations for campaign ${campaignId}`, () =>
    apiClient.post<void>(`/campaigns/${campaignId}/pending-organizations`, selections)
  );
}

/**
 * Fetch pending organization selections from backend
 *
 * Retrieves saved selection state (preview, final, removal) for a campaign.
 * Used to restore user selections after browser refresh.
 *
 * @param campaignId - Campaign ID (e.g., "CAMPAIGN_20251028_121851")
 * @returns Promise resolving to selection state
 *
 * @example
 * ```typescript
 * const selections = await fetchPendingOrganizations('CAMPAIGN_20251028_121851');
 * console.log(selections.final_ids); // [10, 20, 30]
 * ```
 */
export async function fetchPendingOrganizations(
  campaignId: string
): Promise<{
  preview_ids: number[];
  final_ids: number[];
  removal_ids: number[];
}> {
  // Fetch raw data using apiCall wrapper (which extracts response.data)
  const data = await apiCall(`fetch pending organizations for campaign ${campaignId}`, () =>
    apiClient.get<{
      preview_ids: number[];
      final_ids: number[];
      removal_ids: number[];
    }>(`/campaigns/${campaignId}/pending-organizations`)
  );

  // Guard against undefined/null response data - provide sensible defaults
  if (!data) {
    console.warn(`[OrganizationAPI] No pending selections found for campaign ${campaignId}, returning empty defaults`);
    return {
      preview_ids: [],
      final_ids: [],
      removal_ids: [],
    };
  }

  // Ensure all fields exist with defaults
  return {
    preview_ids: data.preview_ids || [],
    final_ids: data.final_ids || [],
    removal_ids: data.removal_ids || [],
  };
}
